'use client';
import React, { useEffect, useState } from 'react';
import SinglePostCard from '@/components/blog/SinglePostCard';
import { fetchPaginatedArticles } from '@/sanity/services/articleService';
import { useRouter } from 'next/navigation';
import BlogBottomPagination from './BlogBottomPagination';
import PaginatedPostsLoader from './PaginatedPostsLoader';

interface ResultProps {
  page: number;
  hitsPerPage?: number;
  filter?: string;
  overrideUrl?: string;
  className?: string;
}

export const BlogSearchListDefault: React.FC<ResultProps> = ({
  page,
  hitsPerPage = 20,
  filter = '',
  overrideUrl,
  className = '',
}) => {
  const baseUrl = overrideUrl || '/blog/search';
  const [articles, setArticles] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { total, articles } = await fetchPaginatedArticles(
        page,
        hitsPerPage,
        filter
      );

      if (!articles || articles.length === 0) {
        if (page > 1) {
          // If the page is out of bounds, redirect to the last page
          const lastPage = Math.ceil(total / hitsPerPage) || 1;
          router.push(`${baseUrl}?page=${lastPage}`);
        } else {
          setArticles([]); // No articles found for page 1
        }
      } else {
        setArticles(articles);
        setTotal(total);
      }

      setLoading(false);
    };

    fetchData();
  }, [page, hitsPerPage, filter, router]);

  // Calculate pageFrom and pageTo correctly
  const pageFrom = (page - 1) * hitsPerPage + 1; // The index of the first article on the page
  const pageTo = Math.min(page * hitsPerPage, total); // The index of the last article on the page, capped by the total number of items
  const totalPages = Math.ceil(total / hitsPerPage);

  return (
    <>
      {loading && <PaginatedPostsLoader />}

      <div className={`grid gap-8 sm:grid-cols-2 ${className}`}>
        {articles.length > 0 ? (
          articles.map((article: any) => (
            <SinglePostCard
              key={article._id}
              post={article}
              headingClass='link-underline'
              className='w-1/2 [&_.content]:w-[75%]'
            />
          ))
        ) : (
          <p className='fallback'>No articles found.</p>
        )}
      </div>

      {total > 0 && (
        <BlogBottomPagination
          pageFrom={pageFrom}
          pageTo={pageTo}
          totalHits={total}
          currentPage={page}
          searchQuery=''
          numberOfPages={totalPages}
          tag='articles'
          overrideUrl={baseUrl}
        />
      )}
    </>
  );
};
